// DeckFrame.tsx — a pitch deck, playable ON the board.
//
// The sibling of <DocumentFrame>. Same idea, different block library: a
// pitch-doc row rendered with pitchDeckConfig instead of architectureConfig.
// The `Deck` block brings its own slide state, arrows, dot indicators and (when
// the recipe sets audioBase) play button — so click-through and play work
// inline on the canvas, exactly as they do at /pitch-doc/*.
//
// Deliberately NOT an iframe of the /pitch-doc route, and it does not touch the
// shared Layout — an `?embed=1` early-return in Layout hung the entire dashboard
// in July 2026. Rendering the blocks inline is the sanctioned way to embed a
// deck chrome-less (same approach as the MAX walkthrough).
//
// Carries the same two React-Flow traps as DocumentFrame: pointerEvents auto
// (RF sets pointer-events:none on non-draggable nodes) and nowheel/nodrag/nopan
// so the deck's own scrolling and clicks work without panning the canvas.

import { useEffect, useState } from "react";
import { BlockRenderer, type Block } from "../blueprint/BlockRenderer";
import { pitchDeckConfig } from "../blueprint/PitchDeckBlocks";
import { useApi } from "../../../lib/api";

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

export function DeckFrame({
  slug,
  label,
  width = 1500,
  height = 900,
}: {
  /** page_content slug, e.g. "pitch-doc/consultancy/falcon/first-meeting" */
  slug: string;
  label?: string;
  width?: number;
  height?: number;
}) {
  const api = useApi();
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBlocks(null);
    setError(null);
    (async () => {
      try {
        const res = await api(`/api/kb/${slug}`);
        if (!res.ok) {
          if (!cancelled) setError(`${res.status} — could not load ${slug}`);
          return;
        }
        const body = (await res.json()) as { content?: { blocks?: Block[] } };
        if (cancelled) return;
        if (Array.isArray(body.content?.blocks)) setBlocks(body.content!.blocks!);
        else setError(`no blocks at ${slug}`);
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // `api` is intentionally omitted — useApi() returns a fresh closure every
    // render, so including it re-fires this effect forever (see DocumentFrame).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  return (
    <div style={{ width, fontFamily: FONT, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, paddingInline: 4 }}>
        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8C948E" }}>
          {label ?? "Deck"}
        </div>
        <a
          href={`/${slug}`}
          className="nodrag nopan"
          style={{ pointerEvents: "auto", fontFamily: MONO, fontSize: 11, color: "#8C948E", textDecoration: "none", whiteSpace: "nowrap" }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          open full screen ↗
        </a>
      </div>

      <div
        className="nowheel nodrag nopan"
        data-zoom
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          pointerEvents: "auto",
          width,
          height,
          overflow: "hidden",
          background: "#FFFFFF",
          border: "1px solid #E3DED4",
          borderRadius: 18,
          boxShadow: "0 10px 30px rgba(27,33,30,0.05)",
          boxSizing: "border-box",
        }}
      >
        {error && <div style={{ padding: 40, fontFamily: MONO, fontSize: 13, color: "#C0492F" }}>{error}</div>}
        {!error && !blocks && <div style={{ padding: 40, fontFamily: MONO, fontSize: 13, color: "#8C948E" }}>loading…</div>}
        {blocks && <BlockRenderer config={pitchDeckConfig} blocks={blocks} />}
      </div>
    </div>
  );
}
